import {
  CALCULATOR_DEFAULTS,
  type BlendCalculationOutcome,
  type CalculationMode,
  type SingleStrainFromWeightOutcome,
  type SingleStrainOutcome,
} from '@shared';
import {
  CALCULATOR_MAX_STRAINS,
  addSelectedStrainId,
  calculatorExperience,
  type CalculatorExperience,
  removeSelectedStrainId,
} from './calculatorExperience';

/**
 * Session state for the calculator page.
 * Selection changes always clear blend/single outputs so results never describe
 * a stale strain set — including replacements that stay in blend mode.
 *
 * `calculationEpoch` is also bumped when calculation inputs change so in-flight
 * Calculate responses and pending wheel-transfer hydration cannot reappear
 * against edited targets/weights.
 */

/** Single-strain: solve from joint weight or from target THC (mg). */
export type SingleTargetKind = 'target_weight' | 'target_mg';

/** Confirmed single-strain Calculate result (not a live preview). */
export type ConfirmedSingleStrainResult =
  | { kind: 'target_mg'; outcome: SingleStrainOutcome }
  | { kind: 'target_weight'; outcome: SingleStrainFromWeightOutcome };

export type CalculatorSessionState = {
  selectedIds: string[];
  /**
   * Monotonic counter bumped whenever the selected strain set changes.
   * Kept for selection-identity diagnostics; blend result acceptance uses
   * `calculationEpoch`.
   */
  selectionEpoch: number;
  /**
   * Monotonic counter bumped when selection **or** calculation-affecting
   * inputs change. In-flight blend responses and transfer hydration must
   * carry the epoch captured at start; mismatches are ignored.
   */
  calculationEpoch: number;
  target: string;
  weight: string;
  precision: number;
  /** Rounding strategy for blend / Target MG single-strain solves. */
  mode: CalculationMode;
  /** Which single-strain solve the user is performing. */
  singleTargetKind: SingleTargetKind;
  rangeMin: string;
  rangeMax: string;
  useMaxThc: boolean;
  maxThc: string;
  blendOutcome: BlendCalculationOutcome | null;
  singleOutcome: ConfirmedSingleStrainResult | null;
  calcError: string | null;
  saveMsg: string | null;
  note: string;
  adding: boolean;
};

export type CalculatorSessionAction =
  | { type: 'select_first'; strainId: string }
  | { type: 'add_strain'; strainId: string }
  | { type: 'replace_strain'; index: number; strainId: string }
  | { type: 'remove_strain'; strainId: string }
  | { type: 'begin_add' }
  | { type: 'cancel_add' }
  | { type: 'set_target'; value: string }
  | { type: 'set_weight'; value: string }
  | { type: 'set_precision'; value: number }
  | { type: 'set_mode'; value: CalculationMode }
  | { type: 'set_single_target_kind'; value: SingleTargetKind }
  | { type: 'set_range_min'; value: string }
  | { type: 'set_range_max'; value: string }
  | { type: 'set_use_max_thc'; value: boolean }
  | { type: 'set_max_thc'; value: string }
  | {
      type: 'set_blend_outcome';
      outcome: BlendCalculationOutcome | null;
      expectedEpoch: number;
    }
  | {
      type: 'set_single_outcome';
      outcome: ConfirmedSingleStrainResult | null;
      expectedEpoch: number;
    }
  | {
      type: 'set_calc_error';
      message: string | null;
      expectedEpoch?: number;
    }
  | { type: 'set_save_msg'; message: string | null }
  | { type: 'set_note'; value: string }
  | { type: 'reset_blend_output' }
  | { type: 'reset_single_output' };

export type CalculatorUiFlags = {
  experience: CalculatorExperience;
  showEmptySetup: boolean;
  showSingleCalculator: boolean;
  showBlendCalculator: boolean;
  showSingleResult: boolean;
  showBlendResult: boolean;
  showAddStrainControl: boolean;
};

export function createInitialCalculatorSession(
  initialIds: string[] = [],
  overrides: {
    target?: string | null;
    weight?: string | null;
    blendOutcome?: BlendCalculationOutcome | null;
    singleTargetKind?: SingleTargetKind;
    singleOutcome?: ConfirmedSingleStrainResult | null;
  } = {},
): CalculatorSessionState {
  const defaultKind: SingleTargetKind =
    overrides.singleTargetKind ??
    (initialIds.length === 1 &&
    overrides.target != null &&
    overrides.target !== ''
      ? 'target_mg'
      : 'target_weight');

  return {
    selectedIds: [...initialIds],
    selectionEpoch: 0,
    calculationEpoch: 0,
    target:
      overrides.target != null && overrides.target !== ''
        ? overrides.target
        : String(CALCULATOR_DEFAULTS.targetThcMg),
    weight:
      overrides.weight != null && overrides.weight !== ''
        ? Number.isFinite(Number(overrides.weight))
          ? Number(overrides.weight).toFixed(2)
          : overrides.weight
        : CALCULATOR_DEFAULTS.totalWeightGrams.toFixed(2),
    precision: CALCULATOR_DEFAULTS.scalePrecisionGrams,
    mode: 'closest',
    singleTargetKind: defaultKind,
    rangeMin: '55',
    rangeMax: '75',
    useMaxThc: false,
    maxThc: '70',
    blendOutcome: overrides.blendOutcome ?? null,
    singleOutcome: overrides.singleOutcome ?? null,
    calcError: null,
    saveMsg: null,
    note: '',
    adding: false,
  };
}

/** Clear confirmed calculation surfaces while preserving shared inputs. */
function clearCalculationOutputs(): Pick<
  CalculatorSessionState,
  'blendOutcome' | 'singleOutcome' | 'calcError' | 'saveMsg'
> {
  return {
    blendOutcome: null,
    singleOutcome: null,
    calcError: null,
    saveMsg: null,
  };
}

function withSelection(
  state: CalculatorSessionState,
  selectedIds: string[],
  extras: Partial<CalculatorSessionState> = {},
): CalculatorSessionState {
  return {
    ...state,
    ...clearCalculationOutputs(),
    selectedIds,
    selectionEpoch: state.selectionEpoch + 1,
    calculationEpoch: state.calculationEpoch + 1,
    adding: false,
    ...extras,
  };
}

/** Clear outputs and invalidate pending calculate / transfer hydration. */
function withCalculationInput(
  state: CalculatorSessionState,
  patch: Partial<CalculatorSessionState>,
): CalculatorSessionState {
  return {
    ...state,
    ...clearCalculationOutputs(),
    calculationEpoch: state.calculationEpoch + 1,
    ...patch,
  };
}

export function calculatorSessionReducer(
  state: CalculatorSessionState,
  action: CalculatorSessionAction,
): CalculatorSessionState {
  switch (action.type) {
    case 'select_first':
      return withSelection(state, [action.strainId]);

    case 'add_strain': {
      const selectedIds = addSelectedStrainId(
        state.selectedIds,
        action.strainId,
      );
      if (selectedIds.join('|') === state.selectedIds.join('|')) {
        return { ...state, adding: false };
      }
      return withSelection(state, selectedIds);
    }

    case 'replace_strain': {
      if (
        action.index < 0 ||
        action.index >= state.selectedIds.length ||
        state.selectedIds[action.index] === action.strainId
      ) {
        return { ...state, adding: false };
      }
      if (
        state.selectedIds.includes(action.strainId) &&
        state.selectedIds[action.index] !== action.strainId
      ) {
        // Duplicate elsewhere — ignore.
        return state;
      }
      const selectedIds = [...state.selectedIds];
      selectedIds[action.index] = action.strainId;
      return withSelection(state, selectedIds);
    }

    case 'remove_strain': {
      const selectedIds = removeSelectedStrainId(
        state.selectedIds,
        action.strainId,
      );
      if (selectedIds.join('|') === state.selectedIds.join('|')) {
        return state;
      }
      return withSelection(state, selectedIds);
    }

    case 'begin_add':
      if (state.selectedIds.length === 0) return state;
      if (state.selectedIds.length >= CALCULATOR_MAX_STRAINS) return state;
      return { ...state, adding: true };

    case 'cancel_add':
      return { ...state, adding: false };

    case 'set_target':
      if (state.target === action.value) return state;
      return withCalculationInput(state, { target: action.value });

    case 'set_weight':
      if (state.weight === action.value) return state;
      return withCalculationInput(state, { weight: action.value });

    case 'set_precision':
      if (state.precision === action.value) return state;
      return withCalculationInput(state, { precision: action.value });

    case 'set_mode':
      if (state.mode === action.value) return state;
      return withCalculationInput(state, { mode: action.value });

    case 'set_single_target_kind':
      if (state.singleTargetKind === action.value) return state;
      return withCalculationInput(state, {
        singleTargetKind: action.value,
      });

    case 'set_range_min':
      if (state.rangeMin === action.value) return state;
      return withCalculationInput(state, { rangeMin: action.value });

    case 'set_range_max':
      if (state.rangeMax === action.value) return state;
      return withCalculationInput(state, { rangeMax: action.value });

    case 'set_use_max_thc':
      if (state.useMaxThc === action.value) return state;
      return withCalculationInput(state, { useMaxThc: action.value });

    case 'set_max_thc':
      if (state.maxThc === action.value) return state;
      return withCalculationInput(state, { maxThc: action.value });

    case 'set_blend_outcome':
      if (action.expectedEpoch !== state.calculationEpoch) {
        return state;
      }
      return {
        ...state,
        blendOutcome: action.outcome,
        calcError: null,
      };

    case 'set_single_outcome':
      if (action.expectedEpoch !== state.calculationEpoch) {
        return state;
      }
      return {
        ...state,
        singleOutcome: action.outcome,
        calcError: null,
      };

    case 'set_calc_error':
      if (
        action.expectedEpoch !== undefined &&
        action.expectedEpoch !== state.calculationEpoch
      ) {
        return state;
      }
      return { ...state, calcError: action.message };

    case 'set_save_msg':
      return { ...state, saveMsg: action.message };

    case 'set_note':
      return { ...state, note: action.value };

    case 'reset_blend_output':
      return { ...state, ...clearCalculationOutputs(), note: '' };

    case 'reset_single_output':
      return {
        ...state,
        singleOutcome: null,
        calcError: null,
      };

    default:
      return state;
  }
}

export function calculatorUiFlags(
  state: CalculatorSessionState,
): CalculatorUiFlags {
  const experience = calculatorExperience(state.selectedIds.length);
  return {
    experience,
    showEmptySetup: experience === 'empty',
    showSingleCalculator: experience === 'single',
    showBlendCalculator: experience === 'blend',
    showSingleResult:
      experience === 'single' && state.singleOutcome !== null,
    showBlendResult: experience === 'blend' && state.blendOutcome !== null,
    showAddStrainControl:
      state.selectedIds.length > 0 &&
      state.selectedIds.length < CALCULATOR_MAX_STRAINS &&
      !state.adding,
  };
}
